import { SafePromise } from '@benzinga/safe-await';
import { Subscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';

import {
  GovernmentTrade,
  TradingPerformance,
  MembersOverview,
  GovernmentMember,
  Overview,
  MonthlyOverview,
  Security,
  SecuritiesOverview,
  TradesQuery,
} from './entities';
import { GovTradesRestful } from './restful';
import { GovTradesManagerEvent } from './manager';

export class GovTradesRequest extends Subscribable<GovTradesManagerEvent> {
  private restful: GovTradesRestful;

  constructor(session: Session) {
    super();
    this.restful = new GovTradesRestful(session);
  }

  public getTrades = async (query: TradesQuery = {}): SafePromise<{ data: GovernmentTrade[] }> => {
    const response = await this.restful.getTrades(query);
    if (response.err) {
      this.dispatch({
        error: response.err,
        type: `gov_trades:get_trades_error`,
      });
    } else {
      this.dispatch({
        trades: response.ok.data,
        type: 'gov_trades:update_trades',
      });
    }
    return response;
  };

  public getMembers = async (): SafePromise<MembersOverview[]> => {
    const members = await this.restful.getMembers();
    if (members.err) {
      this.dispatch({
        error: members.err,
        type: 'gov_trades:get_members_error',
      });
    }
    return members;
  };

  public getMember = async (id: string): SafePromise<GovernmentMember> => {
    const response = await this.restful.getMember(id);
    if (response.err) {
      this.dispatch({
        error: response.err,
        type: 'gov_trades:get_members_error',
      });
      return { err: response.err };
    } else {
      return { ok: response.ok[0] };
    }
  };

  public getReports = async (): SafePromise<{ data: GovernmentTrade[] }> => {
    const response = await this.restful.getReports();
    if (response.err) {
      this.dispatch({
        error: response.err,
        type: 'gov_trades:get_reports_error',
      });
    } else {
      this.dispatch({
        reports: response.ok.data,
        type: 'gov_trades:get_reports',
      });
    }
    return response;
  };

  public getOverview = async (): SafePromise<Overview[]> => {
    const response = await this.restful.getOverview();
    if (response.err) {
      this.dispatch({
        error: response.err,
        type: `gov_trades:get_overview_error`,
      });
    } else {
      this.dispatch({
        overview: response.ok,
        type: 'gov_trades:get_overview',
      });
    }
    return response;
  };

  public getOverviewMonthly = async (): SafePromise<MonthlyOverview[]> => {
    const response = await this.restful.getOverviewMonthly();
    if (response.err) {
      this.dispatch({
        error: response.err,
        type: `gov_trades:get_overview_monthly_error`,
      });
    } else {
      this.dispatch({
        overview: response.ok,
        type: 'gov_trades:get_overview_monthly',
      });
    }
    return response;
  };

  public getSecurity = async (symbol: string): SafePromise<Security[]> => {
    const response = await this.restful.getSecurity(symbol);
    if (response.err) {
      this.dispatch({
        error: response.err,
        type: `gov_trades:get_security_error`,
      });
    } else {
      this.dispatch({
        security: response.ok,
        type: 'gov_trades:get_security',
      });
    }
    return response;
  };

  public getSecurities = async (): SafePromise<SecuritiesOverview[]> => {
    const response = await this.restful.getSecurities();
    if (response.err) {
      this.dispatch({
        error: response.err,
        type: `gov_trades:get_securities_error`,
      });
    } else {
      this.dispatch({
        securities: response.ok,
        type: 'gov_trades:get_securities',
      });
    }
    return response;
  };

  public getCongressTradingPerformance = async (): SafePromise<TradingPerformance[]> => {
    const response = await this.restful.getCongressTradingPerformance();
    if (response.err) {
      this.dispatch({
        error: response.err,
        type: `gov_trades:get_trading_performance_error`,
      });
    } else {
      this.dispatch({
        tradingPerformance: response.ok,
        type: 'gov_trades:get_congress_trading_performance',
      });
    }
    return response;
  };

  public getTradingPerformanceByMember = async (id: string): SafePromise<TradingPerformance[]> => {
    const response = await this.restful.getTradingPerformanceByMember(id);
    if (response.err) {
      this.dispatch({
        error: response.err,
        type: `gov_trades:update_member_trades_error`,
      });
    } else {
      this.dispatch({
        tradingPerformance: response.ok,
        type: 'gov_trades:update_member_trades',
      });
    }
    return response;
  };
}
